import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ImCross } from "react-icons/im";
import { FaQuoteLeft } from "react-icons/fa";
import { FiCheck, FiStar } from "react-icons/fi";
import Button from "../NavBar/Button";
import NavLinks from "../NavBar/NavLinks";
import Form from "./Form";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper";
import "swiper/css";

const testimonials = [
  {
    text: "Abscod has been an excellent partner for our digital marketing needs. Their team is responsive, knowledgeable, and truly understands our industry.",
    name: "Gaurav Munjal",
    company: "Cred",
    image: "/Images/client3.png",
  },
  {
    text: "We had a complex software development project that Abscod handled with ease. Their team is highly skilled and dependable.",
    name: "Aditya",
    company: "Shape Me Now",
    image: "/Images/client1.png",
  },
  {
    text: "Abscod created a beautiful, functional website for our business. They paid attention to every detail and understood our brand.",
    name: "Abhik Nandi",
    company: "Abhik Nandi Pvt. Ltd.",
    image: "/Images/client2.png",
  },
];

const StarRating = () => (
  <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, index) => (
      <FiStar key={index} className="h-4 w-4 fill-[#FFDB16] text-[#FFDB16]" aria-hidden="true" />
    ))}
  </div>
);

const FreeQuote = ({ popHandler }) => {
  const swiperRef = useRef(null);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] isolate h-screen overflow-hidden bg-[#081120] text-white [height:100dvh]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-dialog-title"
    >
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-blue-500/10 blur-[120px]" />
        <div className="absolute -right-40 bottom-0 h-[30rem] w-[30rem] rounded-full bg-[#FFDB16]/10 blur-[140px]" />
        <div className="absolute inset-0 opacity-[0.035] [background-image:linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)] [background-size:56px_56px]" />
      </div>

      <div className="relative mx-auto flex h-full w-full max-w-[1500px] flex-col px-4 pt-[84px] sm:px-6 lg:px-10">
        <header className="fixed inset-x-0 top-0 z-[60] h-[84px] border-b border-slate-200 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.12)]">
          <div className="mx-auto flex h-full w-full max-w-[1480px] items-center justify-between px-4 sm:px-6 lg:px-10">
            <Link to="/" className="inline-flex shrink-0" aria-label="Abscod home">
              <img
                src="/Images/abscodblack.webp"
                alt="Abscod"
                width="160"
                className="h-16 w-auto object-contain"
              />
            </Link>

            <nav className="hidden text-slate-900 md:block" aria-label="Quote form navigation">
              <ul className="flex items-center gap-8 text-lg">
                <li>
                  <Link to="/" className="inline-block px-3 py-7">Home</Link>
                </li>
                <NavLinks />
                <li>
                  <Link to="/aboutus" className="inline-block px-3 py-7">About us</Link>
                </li>
                <li>
                  <Link to="/faq" className="inline-block px-3 py-7">FAQs</Link>
                </li>
                <li>
                  <Link to="/contactus" className="inline-block px-3 py-7">Contact us</Link>
                </li>
              </ul>
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden md:block">
                <Button
                  color="black"
                  content="Request a Quote"
                  background="linear-gradient(to right, #FFDB16, #FFDB16)"
                />
              </div>
              <button
                onClick={popHandler}
                type="button"
                aria-label="Close quote form"
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-100 transition hover:border-[#e0bd00] hover:bg-[#FFDB16] sm:h-11 sm:w-11"
              >
                <ImCross className="h-3.5 w-3.5 text-slate-700 transition group-hover:text-slate-950" />
              </button>
            </div>
          </div>
        </header>

        <main className="quote-dialog-main flex min-h-0 flex-1 flex-col justify-center py-3 sm:py-5">
          <div className="quote-dialog-intro mx-auto mb-4 max-w-3xl shrink-0 text-center sm:mb-6">
            <p className="mb-1.5 hidden text-xs font-bold uppercase tracking-[0.22em] text-[#FFDB16] sm:block">
              Let&apos;s work together
            </p>
            <h2
              id="quote-dialog-title"
              className="text-2xl font-extrabold tracking-[-0.04em] text-white sm:text-3xl lg:text-4xl"
            >
              Have an idea? Let&apos;s make it real.
            </h2>
            <p className="mx-auto mt-1 max-w-2xl text-xs leading-5 text-slate-300 sm:mt-2 sm:text-sm sm:leading-6">
              Tell us what you&apos;re planning. We&apos;ll bring the strategy, design and technology to move it forward.
            </p>
          </div>

          <div className="mx-auto grid min-h-0 w-full max-w-[1400px] flex-1 items-stretch gap-4 sm:gap-6 xl:grid-cols-[minmax(0,0.82fr)_minmax(500px,1.32fr)_minmax(0,0.82fr)]">
            <aside className="hidden min-h-0 min-w-0 overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.07] p-6 backdrop-blur-sm xl:flex xl:flex-col">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FFDB16]">Client stories</p>
                  <h3 className="mt-2 text-xl font-bold text-white">Trusted by teams</h3>
                </div>
                <div className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200">
                  4.9 / 5
                </div>
              </div>

              <div className="flex min-h-0 min-w-0 flex-1 items-center overflow-hidden">
                <Swiper
                  onMouseEnter={() => swiperRef.current?.autoplay.stop()}
                  onMouseLeave={() => swiperRef.current?.autoplay.start()}
                  loop
                  autoplay={{ delay: 3500, disableOnInteraction: false }}
                  modules={[Autoplay]}
                  className="!m-0 !w-full !min-w-0 !overflow-hidden"
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                >
                  {testimonials.map((testimonial) => (
                    <SwiperSlide key={testimonial.name} className="!w-full !min-w-0">
                      <article className="min-w-0 overflow-hidden">
                        <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFDB16] text-slate-950">
                          <FaQuoteLeft className="text-lg" aria-hidden="true" />
                        </div>
                        <p className="max-w-full break-words text-[15px] leading-7 text-slate-200">
                          “{testimonial.text}”
                        </p>
                        <div className="my-6 h-px bg-white/10" />
                        <StarRating />
                        <div className="mt-5 flex min-w-0 items-center gap-3">
                          <img
                            src={testimonial.image}
                            alt=""
                            className="h-12 w-12 shrink-0 rounded-full border-2 border-[#FFDB16] object-cover"
                          />
                          <div className="min-w-0">
                            <h4 className="truncate text-sm font-bold text-white">{testimonial.name}</h4>
                            <p className="truncate text-xs text-slate-400">{testimonial.company}</p>
                          </div>
                        </div>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </aside>

            <section className="mx-auto min-h-0 min-w-0 w-full max-w-2xl">
              <Form />
            </section>

            <aside className="hidden min-h-0 min-w-0 overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.07] p-6 backdrop-blur-sm xl:flex xl:flex-col">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FFDB16]">Recognition</p>
                <h3 className="mt-2 text-xl font-bold text-white">Award-winning work</h3>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Recognized for turning ambitious ideas into memorable digital experiences.
                </p>
              </div>

              <div className="my-auto flex justify-center py-4">
                <div className="relative">
                  <div className="absolute inset-2 rounded-full bg-[#FFDB16]/20 blur-2xl" aria-hidden="true" />
                  <div className="relative rounded-3xl border border-white/10 bg-[#0d1927] p-5 shadow-2xl">
                    <img
                      src="/Images/freeqimg1.png"
                      alt="Clutch Top Corporate Branding Company India 2022"
                      className="w-32 object-contain 2xl:w-36"
                    />
                  </div>
                </div>
              </div>

              <ul className="space-y-2.5 border-t border-white/10 pt-5 text-sm text-slate-200">
                {["Strategy-led approach", "Dedicated project team", "Clear, timely communication"].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFDB16]/15 text-[#FFDB16]">
                      <FiCheck aria-hidden="true" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          <div className="mx-auto mt-3 hidden shrink-0 flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-slate-400 sm:flex xl:hidden">
            <span className="flex items-center gap-2"><FiCheck className="text-[#FFDB16]" /> Free consultation</span>
            <span className="flex items-center gap-2"><FiCheck className="text-[#FFDB16]" /> Reply within one business day</span>
            <span className="flex items-center gap-2"><FiCheck className="text-[#FFDB16]" /> No obligation</span>
          </div>
        </main>
      </div>
    </div>,
    document.body
  );
};

export default FreeQuote;
